import {
  BubbleButton,
  BubbleCurrency,
  BubbleFormGroup,
  BubbleHintSelect,
  BubbleInput,
  BubbleSelect,
  BubbleTable,
} from '@go-client/components/BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import { formatCentsToUSD } from '@go-shared/lib/utils';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import {
  addMinutesToTime,
  calculateTimeDifference,
  formatTimeRange,
  isoLocal,
} from '@go-shared/lib/time';
import {
  RESERVABLE_EXPERIENCE_TYPE,
  ReservableEventTemplateSnapshot,
} from '@go-shared/types/reservable';
import {
  ReserveScheduleSlot,
  ReserveScheduleSlotLimits,
  ReserveScheduleSlotPriceOverrides,
} from '@go-shared/types/reserveSchedule';
import { ObjectId } from 'bson';
import { FormikProps, getIn } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { ReservableScheduleFormikForm } from '../Formik/ReservableScheduleFormik';

interface ReservableTimeSlotProps {
  disabled?: boolean;
  readOnly?: boolean;
  experienceType: RESERVABLE_EXPERIENCE_TYPE;
  eventSnapshot: ReservableEventTemplateSnapshot;
  formik: FormikProps<ReservableScheduleFormikForm>;
  ruleIdx: number;
}

interface SlotTableItem {
  _id: string;
  label?: string;
  time: string;
  capacity?: number;
  limits: string;
  priceOverrides: string;
  exceptions: string;
  hasErrors: boolean;
  _slot: ReserveScheduleSlot;
}

export function ReservableTimeSlots(props: ReservableTimeSlotProps) {
  const { disabled, readOnly, formik } = props;

  const { values, setFieldValue, handleBlur } = formik;
  const rule = values.rules[props.ruleIdx];

  const [selectedSlotIdx, setSelectedSlotIdx] = useState(-1);
  const selectedSlot = rule.slots[selectedSlotIdx] as ReserveScheduleSlot | undefined;

  const { tickets, packages, addons, experienceType } = props.eventSnapshot.reservables;
  const isResourceType = experienceType === 'resource';

  const ticketsMap = mapBy(tickets, t => t._id);
  const packagesMap = mapBy(packages, p => p._id);
  const addonsMap = mapBy(addons, a => a._id);

  const limitOpts = [
    ...tickets.map(t => t._id),
    // ...packages.map(p => p._id),
    // ...addons.map(a => a._id),
  ];

  function getAvailableLimitOpts(limitsInSlot: ReserveScheduleSlotLimits | undefined) {
    if (!limitsInSlot) return limitOpts;
    const usedOpts = Object.keys(limitsInSlot);
    return limitOpts.filter(o => !usedOpts.includes(o));
  }

  function nextAvailableLimitOpt(
    limitsInSlot: ReserveScheduleSlotLimits | undefined
  ): string | undefined {
    if (!limitsInSlot) return limitOpts[0];
    const availableOpts = getAvailableLimitOpts(limitsInSlot);
    return availableOpts[0];
  }

  const priceOverrideOpts = tickets.map(t => t._id);

  function getAvailablePriceOverrideOpts(
    overridesInSlot: ReserveScheduleSlotPriceOverrides | undefined,
    currentKey?: string
  ) {
    const usedOpts = Object.keys(overridesInSlot || {}).filter(k => k !== currentKey);
    return priceOverrideOpts.filter(o => !usedOpts.includes(o));
  }

  function nextAvailablePriceOverrideOpt(
    overridesInSlot: ReserveScheduleSlotPriceOverrides | undefined
  ): string | undefined {
    return getAvailablePriceOverrideOpts(overridesInSlot)[0];
  }

  function getRelevantExceptions(slot: ReserveScheduleSlot) {
    return slot.exceptions ?? [];
  }

  const items: SlotTableItem[] = (rule.slots || []).map((slot, i) => {
    const limitsCount = Object.keys(slot.limits || {}).length;
    const slotErrors = getIn(formik.errors, `rules[${props.ruleIdx}].slots[${i}]`);
    const item: SlotTableItem = {
      _id: slot._id,
      label: slot.label,
      time: formatTimeRange(slot.time, slot.duration),
      capacity: slot.capacity,
      limits: `${limitsCount} limits`,
      priceOverrides: `${Object.keys(slot.priceOverrides || {}).length} overrides`,
      exceptions: `${getRelevantExceptions(slot).length} exceptions`,
      hasErrors: !!slotErrors,
      _slot: slot,
    };

    return item;
  });

  function makeDefaultTemplateTdProps(
    data: SlotTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const expanded = selectedSlotIdx >= 0 && selectedSlot?._id === data._id;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        expanded ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  const tableFields: Array<BubbleTableFields<SlotTableItem>> = [
    { key: 'label' },
    { key: 'time' },
    { key: 'capacity' },
    { key: 'limits' },
    { key: 'priceOverrides' },
    { key: 'exceptions' },
    { key: 'hasErrors', label: '', behavior: BubbleTableFieldBehavior.MinWidth },
    { key: 'actions', label: '', behavior: BubbleTableFieldBehavior.Condense },
  ];

  const tableTemplates: Array<BubbleTableTemplate<SlotTableItem>> = [
    { key: 'label', tdProps: makeDefaultTemplateTdProps },
    { key: 'time', tdProps: makeDefaultTemplateTdProps },
    { key: 'capacity', tdProps: makeDefaultTemplateTdProps },
    { key: 'limits', tdProps: makeDefaultTemplateTdProps },
    { key: 'priceOverrides', tdProps: makeDefaultTemplateTdProps },
    { key: 'exceptions', tdProps: makeDefaultTemplateTdProps },
    {
      key: 'hasErrors',
      tdProps: makeDefaultTemplateTdProps,
      render: item =>
        item.hasErrors ? (
          <div className="flex items-center text-crimson font-semibold">
            <FontAwesomeIcon icon="exclamation-triangle" />
            <span className="ml-1">Incomplete</span>
          </div>
        ) : (
          <span></span>
        ),
    },
    {
      key: 'actions',
      tdProps: makeDefaultTemplateTdProps,
      render: (_, idx) => (
        <div className="flex items-center">
          <BubbleButton
            variant="navy"
            className="p-1-i"
            inverse
            icon="edit"
            onClick={() => setSelectedSlotIdx(idx === selectedSlotIdx ? -1 : idx)}
          />
          <BubbleButton
            variant="slate"
            inverse
            className="p-2-i"
            icon="arrow-up"
            disabled={readOnly || idx === 0}
            onClick={() => {
              const newSlots = [...rule.slots];
              const temp = newSlots[idx - 1];
              newSlots[idx - 1] = newSlots[idx];
              newSlots[idx] = temp;
              setFieldValue(`rules[${props.ruleIdx}].slots`, newSlots);
              if (selectedSlotIdx === idx) {
                setSelectedSlotIdx(idx - 1);
              }
            }}
          />
          <BubbleButton
            variant="slate"
            inverse
            className="p-2-i"
            icon="arrow-down"
            disabled={readOnly || idx === rule.slots.length - 1}
            onClick={() => {
              const newSlots = [...rule.slots];
              const temp = newSlots[idx + 1];
              newSlots[idx + 1] = newSlots[idx];
              newSlots[idx] = temp;
              setFieldValue(`rules[${props.ruleIdx}].slots`, newSlots);
              if (selectedSlotIdx === idx) {
                setSelectedSlotIdx(idx + 1);
              }
            }}
          />
          <BubbleButton
            variant="crimson"
            inverse
            className="p-2-i"
            icon="trash-alt"
            disabled={readOnly}
            onClick={() => {
              const newSlots = [...rule.slots];
              newSlots.splice(idx, 1);
              setFieldValue(`rules[${props.ruleIdx}].slots`, newSlots);
              setSelectedSlotIdx(-1);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <fieldset
      disabled={disabled}
      className="bg-white p-4 border rounded shadow grid grid-cols-1 gap-4 justify-items-start"
    >
      {!readOnly && (
        <div className="text-xs text-slate italic">
          Define the time slots available for reservations. Each slot can have its own capacity.
        </div>
      )}
      {items.length > 0 && (
        <BubbleTable
          responsive
          striped
          slim
          hideHeader
          bordered
          items={items}
          fields={tableFields}
          templates={tableTemplates}
          keyExtractor={item => item._id}
          detailsKey={selectedSlot?._id}
          onRowClick={(_, idx) => setSelectedSlotIdx(idx === selectedSlotIdx ? -1 : idx)}
          detailsClassName="p-4 bg-bubblegum-lightest"
          detailsRender={() =>
            selectedSlot && (
              <div className="w-full grid grid-cols-2 gap-4 bg-eggshell shadow p-3 border">
                <BubbleFormGroup label="Start Time" className="flex-grow" asterisk>
                  <BubbleInput
                    type="time"
                    block
                    value={selectedSlot.time}
                    name={`rules[${props.ruleIdx}].slots[${selectedSlotIdx}].time`}
                    onValue={val => {
                      if (!val) return;
                      setFieldValue(`rules[${props.ruleIdx}].slots[${selectedSlotIdx}].time`, val);
                    }}
                    onBlur={handleBlur}
                    readOnly={readOnly}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="Capacity" className="flex-grow" asterisk>
                  <BubbleInput
                    type="number"
                    block
                    min={1}
                    value={selectedSlot.capacity}
                    disabled={props.experienceType === 'resource'}
                    formik={[formik, `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].capacity`]}
                    readOnly={readOnly}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="Label">
                  <BubbleInput
                    type="text"
                    block
                    value={selectedSlot.label}
                    formik={[formik, `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].label`]}
                    readOnly={readOnly}
                    placeholder="e.g. 'Morning Slot', '9am - 12pm', etc."
                    subtitle="Optional. Will use time range as label if left blank."
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="End Time" className="flex-grow">
                  <BubbleInput
                    type="time"
                    block
                    value={
                      selectedSlot.duration
                        ? addMinutesToTime(selectedSlot.time, selectedSlot.duration)
                        : selectedSlot.time
                    }
                    name={`rules[${props.ruleIdx}].slots[${selectedSlotIdx}].duration`}
                    onValue={val => {
                      if (!val) return;
                      setFieldValue(
                        `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].duration`,
                        calculateTimeDifference(val, selectedSlot.time)
                      );
                    }}
                    onBlur={handleBlur}
                    readOnly={readOnly}
                    subtitle="Optional. Keep same as start time for open-ended slot."
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="Price Overrides" className="col-span-2">
                  <div className="bg-white shadow p-4 border rounded grid grid-cols-1 gap-4 justify-items-start">
                    <div className="text-xs text-slate italic">
                      Override the price for specific tickets in this time slot.
                    </div>
                    {Object.entries(selectedSlot.priceOverrides || {}).map(([key, value]) => (
                      <div key={key} className="w-full grid grid-cols-2 gap-4 items-start">
                        <BubbleFormGroup label="Ticket">
                          <BubbleHintSelect
                            value={key}
                            allowUndefined={false}
                            options={getAvailablePriceOverrideOpts(
                              selectedSlot.priceOverrides,
                              key
                            )}
                            labelExtractor={opt =>
                              ticketsMap[opt] ? `Ticket: ${ticketsMap[opt].name}` : opt
                            }
                            readOnly={readOnly}
                            onValue={newKey => {
                              const newOverrides: Record<string, unknown> = {
                                ...selectedSlot.priceOverrides,
                                [newKey]: value,
                              };
                              delete newOverrides[key];
                              setFieldValue(
                                `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].priceOverrides`,
                                newOverrides
                              );
                            }}
                          />
                        </BubbleFormGroup>
                        <div className="flex items-center">
                          <BubbleFormGroup
                            label={`Price ${ticketsMap[key] ? ` — base: ${formatCentsToUSD(ticketsMap[key].price)}` : ''}`}
                            className="flex-grow"
                          >
                            <BubbleCurrency
                              block
                              name={`rules[${props.ruleIdx}].slots[${selectedSlotIdx}].priceOverrides.${key}`}
                              value={value}
                              onValue={val => {
                                if (val === undefined) return;
                                setFieldValue(
                                  `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].priceOverrides.${key}`,
                                  val
                                );
                              }}
                              onBlur={handleBlur}
                              readOnly={readOnly}
                            />
                          </BubbleFormGroup>
                          {!readOnly && (
                            <BubbleButton
                              style={{ alignSelf: 'end' }}
                              variant="crimson"
                              inverse
                              className="-mr-3"
                              icon="trash-alt"
                              disabled={disabled}
                              onClick={() => {
                                const newOverrides = { ...selectedSlot.priceOverrides };
                                delete newOverrides[key];
                                setFieldValue(
                                  `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].priceOverrides`,
                                  newOverrides
                                );
                              }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                    {!readOnly && (
                      <BubbleButton
                        variant="navy"
                        inverse
                        className="p-0-i"
                        icon="plus"
                        disabled={
                          disabled || !nextAvailablePriceOverrideOpt(selectedSlot.priceOverrides)
                        }
                        onClick={() => {
                          const nextKey = nextAvailablePriceOverrideOpt(
                            selectedSlot.priceOverrides
                          );
                          if (!nextKey) return;
                          setFieldValue(
                            `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].priceOverrides`,
                            {
                              ...(selectedSlot.priceOverrides || {}),
                              [nextKey]: ticketsMap[nextKey]?.price ?? 0,
                            }
                          );
                        }}
                      >
                        Add Price Override
                      </BubbleButton>
                    )}
                  </div>
                </BubbleFormGroup>
                {props.experienceType !== 'resource' && (
                  <BubbleFormGroup label="Limits" className="col-span-2">
                    <div className="bg-white shadow p-4 border rounded grid grid-cols-1 gap-4 justify-items-start">
                      <div className="text-xs text-slate italic">
                        Add limits to tickets here. These are soft limits that only impact the guest
                        experience. They have no effect on the actual inventory or availability of
                        the slot.
                      </div>
                      {Object.entries(selectedSlot.limits || {}).map(([key, value]) => (
                        <div key={key} className="w-full grid grid-cols-2 gap-4 items-start">
                          <BubbleFormGroup label="Type">
                            <BubbleHintSelect
                              value={key}
                              allowUndefined={false}
                              options={getAvailableLimitOpts(selectedSlot.limits)}
                              labelExtractor={opt =>
                                ticketsMap[opt]
                                  ? `Ticket: ${ticketsMap[opt].name}`
                                  : packagesMap[opt]
                                    ? `Package: ${packagesMap[opt].name}`
                                    : addonsMap[opt]
                                      ? `Addon: ${addonsMap[opt].name}`
                                      : opt
                              }
                              onValue={newKey => {
                                const newLimits: Record<string, unknown> = {
                                  ...selectedSlot.limits,
                                  [newKey]: value,
                                };
                                delete newLimits[key];
                                setFieldValue(
                                  `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].limits`,
                                  newLimits
                                );
                              }}
                            />
                          </BubbleFormGroup>
                          <div className="flex items-center">
                            <BubbleFormGroup label="Limit" className="flex-grow">
                              <BubbleInput
                                type="number"
                                block
                                min={0}
                                max={selectedSlot.capacity}
                                value={value}
                                formik={[
                                  formik,
                                  `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].limits.${key}`,
                                ]}
                                readOnly={readOnly}
                              />
                            </BubbleFormGroup>
                            {!readOnly && (
                              <BubbleButton
                                style={{ alignSelf: 'end' }}
                                variant="crimson"
                                inverse
                                className="-mr-3"
                                icon="trash-alt"
                                disabled={disabled}
                                onClick={() => {
                                  const newLimits = { ...selectedSlot.limits };
                                  delete newLimits[key];
                                  setFieldValue(
                                    `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].limits`,
                                    newLimits
                                  );
                                }}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                      {!readOnly && (
                        <BubbleButton
                          variant="navy"
                          inverse
                          className="p-0-i"
                          icon="plus"
                          disabled={disabled || !nextAvailableLimitOpt(selectedSlot.limits)}
                          onClick={() => {
                            const nextKey = nextAvailableLimitOpt(selectedSlot.limits);
                            if (!nextKey) return;
                            setFieldValue(
                              `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].limits`,
                              {
                                ...(selectedSlot.limits || {}),
                                [nextKey]: 0,
                              }
                            );
                          }}
                        >
                          Add Limits
                        </BubbleButton>
                      )}
                    </div>
                  </BubbleFormGroup>
                )}
                <BubbleFormGroup label="Exceptions" className="col-span-2">
                  <div className="bg-white shadow p-4 border rounded grid grid-cols-1 gap-4 justify-items-start">
                    <div className="text-xs text-slate italic">
                      Add exceptions to disable this slot on specific dates without affecting the
                      recurring schedule.
                    </div>
                    {getRelevantExceptions(selectedSlot).map((exc, exIdx) => (
                      <div key={exIdx} className="w-full flex items-center">
                        <div className="flex-grow flex gap-4">
                          <BubbleFormGroup label="date" className="flex-grow">
                            <BubbleInput
                              type="date"
                              block
                              min={isoLocal()}
                              value={exc.date}
                              onValue={val => {
                                const newExceptions = [...getRelevantExceptions(selectedSlot)];
                                newExceptions[exIdx].date = val;
                                setFieldValue(
                                  `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].exceptions`,
                                  newExceptions
                                );
                              }}
                              onBlur={handleBlur}
                              readOnly={readOnly}
                            />
                          </BubbleFormGroup>
                          {isResourceType && (
                            <BubbleFormGroup label="Rental" className="flex-grow">
                              <BubbleSelect
                                block
                                allowUndefined
                                labelExtractor={t => ticketsMap[t].name}
                                options={tickets.map(t => t._id)}
                                name="venue"
                                readOnly={readOnly}
                                onValue={val => {
                                  const newExceptions = [...getRelevantExceptions(selectedSlot)];
                                  newExceptions[exIdx].resource = val;
                                  setFieldValue(
                                    `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].exceptions`,
                                    newExceptions
                                  );
                                }}
                                onBlur={handleBlur}
                                value={exc.resource}
                              />
                            </BubbleFormGroup>
                          )}
                        </div>
                        {!readOnly && (
                          <BubbleButton
                            variant="crimson"
                            style={{ alignSelf: 'end' }}
                            inverse
                            className="-mr-3"
                            icon="trash-alt"
                            disabled={disabled}
                            onClick={() => {
                              const newExceptions = [...getRelevantExceptions(selectedSlot)];
                              newExceptions.splice(exIdx, 1);
                              setFieldValue(
                                `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].exceptions`,
                                newExceptions.length > 0 ? newExceptions : undefined
                              );
                            }}
                          />
                        )}
                      </div>
                    ))}
                    {!readOnly && (
                      <BubbleButton
                        variant="navy"
                        inverse
                        className="p-0-i"
                        icon="plus"
                        disabled={disabled}
                        onClick={() => {
                          const newExceptions = [
                            ...getRelevantExceptions(selectedSlot),
                            { date: isoLocal() },
                          ];
                          setFieldValue(
                            `rules[${props.ruleIdx}].slots[${selectedSlotIdx}].exceptions`,
                            newExceptions
                          );
                        }}
                      >
                        Add Exception
                      </BubbleButton>
                    )}
                  </div>
                </BubbleFormGroup>
              </div>
            )
          }
        />
      )}
      {readOnly && !rule.slots.length && <span className="text-smoke italic">None</span>}
      {!readOnly && (
        <BubbleButton
          variant="navy"
          inverse
          className="p-0-i"
          icon="plus"
          disabled={disabled}
          onClick={() => {
            const newSlots = [
              ...rule.slots,
              {
                _id: new ObjectId().toHexString(),
                time: { hour: 9, minute: 0 },
                capacity: props.experienceType === 'resource' ? 1 : 0,
              },
            ];
            setFieldValue(`rules[${props.ruleIdx}].slots`, newSlots);
            setSelectedSlotIdx(newSlots.length - 1);
          }}
        >
          Add Time Slots
        </BubbleButton>
      )}
    </fieldset>
  );
}
